import { useRef } from "react";
import { useParams } from "react-router-dom";

import { useGetApi, useIsNewEntry } from "../../hooks";
import { useDispatch } from "../../redux/hooks";
import { setFeature, setOowFeature } from "../../redux/slices/generatorSlice";
import { getYachtId } from "../../utils";
import { useTriggerAutoSave } from "../hooks/useTriggerAutoSave";
interface Props {
  tag: string | undefined;
  id: string;
}
/**
 * Custom hook to handle ambassador automations based on the provided tag and ID.
 *
 * @param props - An object containing the following properties:
 *   - tag: An optional string representing the type of automation to perform.
 *   - id: A string representing the unique identifier for the automation.
 * @returns A function to execute the automation logic based on the provided value.
 */
const useAmbAutomations = ({ tag, id }: Props) => {
  const dispatch = useDispatch();
  const triggerAutoSave = useTriggerAutoSave();

  const { lbeidOrPartId, logbookId } = useParams();
  const isNewEntry = useIsNewEntry();

  const { refetch } = useGetApi({
    request: "prev-entry-data",
    body: {
      params: {
        lbid: logbookId,
        lbeid: isNewEntry ? "new" : lbeidOrPartId,
        yachtid: getYachtId(),
        type: tag,
      },
    },
    fetchOnInit: false,
  });

  const alreadyFetched = useRef(false);

  return async (value: boolean) => {
    if (tag === "oowCheck") {
      dispatch(
        setOowFeature({
          key: id,
          value,
        }),
      );
    }
    if (tag === "ambGetPreviousData" && value) {
      if (alreadyFetched.current) return;
      alreadyFetched.current = true;

      const prevEntryData = await refetch();
      if (!prevEntryData?.list) return;

      // Process the data to ensure GPS coordinates and security level are properly handled
      const processedData = { ...prevEntryData.list };

      // Ensure GPS coordinates (lat, latNS, long, longEW) maintain their object structure
      const gpsKeys = ["lat", "latNS", "long", "longEW"];
      gpsKeys.forEach(key => {
        if (processedData[key] && typeof processedData[key] === "object") {
          // Keep the GPS coordinate object structure intact
          processedData[key] = processedData[key];
        }
      }); // Handle security level change from unit=sec to unit=list
      if (processedData.seclevel) {
        // Handle different formats for security level data
        if (typeof processedData.seclevel === "string") {
          // If it's a string like "1", "2", "3", convert to proper format
          const secLevelMap = {
            "1": { key: "SEC1", value: "Security Level 1" },
            "2": { key: "SEC2", value: "Security Level 2" },
            "3": { key: "SEC3", value: "Security Level 3" },
          };
          processedData.seclevel =
            secLevelMap[processedData.seclevel as "1" | "2" | "3"] ||
            processedData.seclevel;
        } else if (
          typeof processedData.seclevel === "object" &&
          processedData.seclevel.value
        ) {
          // If it's already an object, ensure it has the right structure
          processedData.seclevel = {
            key: processedData.seclevel.key || processedData.seclevel.chrisKey,
            value: processedData.seclevel.value,
          };
        }
      }

      // Legacy support for 'sec' field
      if (processedData.sec && typeof processedData.sec === "string") {
        // Convert security level to list format if needed
        processedData.sec = {
          value: processedData.sec,
          unit: "list",
        };
      }

      dispatch(
        setFeature({
          key: tag,
          feature: processedData,
        }),
      );
      setTimeout(() => {
        dispatch(setFeature({ key: tag, feature: {} }));
      }, 1000);
      triggerAutoSave();
    }
  };
};
export default useAmbAutomations;
